import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { organDonorService } from '../../../../services/organDonorInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('OrganDonorIntroScreen2');

export const OrganDonorIntroScreen2: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();

  const updateDonorInterest = async (interested: boolean) => {
    try {
      // Get existing record or create new one, then update with interest
      const allRecords = await organDonorService.getAllOrganDonor();
      if (allRecords.length > 0) {
        // Update existing record
        await organDonorService.updateOrganDonor(allRecords[0].id, {
          interestedInBecomingDonor: interested
        });
      } else {
        // Create new record with interest
        await organDonorService.createOrganDonor({
          interestedInBecomingDonor: interested,
          taskId: 12,
        });
      }
    } catch (error) {
      logger.error('Error updating donor interest:', error);
    }
  };

  const markTaskAsCompleted = async () => {
    try {
      await tasksService.completeTask('organ_donation');
    } catch (error) {
      logger.error('Error marking task as completed:', error);
    }
  };

  const handleNo = async () => {
    // User is not interested in becoming a donor
    await updateDonorInterest(false);
    await markTaskAsCompleted();
    // Navigate to MedicalChecklistScreen (completion logic)
    navigation.navigate('MedicalChecklist');
  };

  const handleYes = async () => {
    // User is interested in becoming a donor
    await updateDonorInterest(true);
    navigation.navigate('OrganDonorIntro3');
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.mainContent}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.content}>
          <Text style={styles.title}>Would you be interested in becoming an organ donor?</Text>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, styles.yesButton]}
              onPress={handleYes}
            >
              <Text style={[styles.buttonText, styles.yesText]}>Yes, I'm interested</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.button, styles.noButton]}
              onPress={handleNo}
            >
              <Text style={[styles.buttonText, styles.noText]}>No, not at this time</Text>
            </TouchableOpacity>
          </View>

          <Text style={styles.description}>
            Becoming an organ donor is a personal decision that can save multiple lives. One donor can help up to 8 people through organ donation and enhance the lives of up to 75 people through tissue donation.
          </Text>
        </View>
      </View>
      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  mainContent: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  content: {
    flex: 1,
    padding: ms(20),
    alignItems: 'center',
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: '600',
    color: '#2C4364',
    marginBottom: ms(30),
    textAlign: 'center',
    paddingHorizontal: ms(20),
  },
  description: {
    fontSize: ms(18, 0.3),
    color: '#666666',
    textAlign: 'center',
    marginTop: ms(30),
    paddingHorizontal: ms(20),
    lineHeight: ms(25, 0.3),
  },
  buttonContainer: {
    width: '100%',
    paddingHorizontal: ms(20),
  },
  button: {
    width: '100%',
    borderRadius: ms(30),
    padding: ms(15),
    alignItems: 'center',
    marginBottom: ms(15),
  },
  yesButton: {
    backgroundColor: '#29BFC0',
  },
  noButton: {
    backgroundColor: '#FF9F64',
  },
  buttonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
  },
  yesText: {
    color: '#FFFFFF',
  },
  noText: {
    color: '#FFFFFF',
  },
});
